import React from 'react';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';

import { LoadMoreProps, CoreSelectProps } from '../types/props';

import { SelectList, GeneralKey, getSelectItemState } from '@app/store/SelectList/SelectList';
import { SelectRequestParams, ActionCreatorProps, getActionCreators } from '@app/store/SelectList/ListActions';
import { SelectRequestProps } from '@app/store/SelectList/SelectsInterfaces';
import { selectListRequest } from '@app/store/SelectList/SelectListRequests';
import { StoreType } from '@app/store/SelectList/UtilityTypes';
import { ApplicationState } from '@app/store';

export function useLoadMore<
	TValue,
	TStore extends keyof SelectList,
	TParams extends SelectRequestParams<TValue>,
>(props: CoreSelectProps<TValue, TStore, TParams>, count: number = 20): LoadMoreProps {
	const reqProps: SelectRequestProps<TStore> = selectListRequest[props.store];
	const key = props.storeKey ?? GeneralKey;
	const root: StoreType<TStore> = useSelector<ApplicationState, StoreType<TStore>>((state: ApplicationState) =>
		state.selects[props.store]);
	const store = getSelectItemState(root, key);

	const dispatch = useDispatch();
	const factory = React.useMemo(
		() => {
			const creatorProps: ActionCreatorProps<TStore> = {
				endpoint: reqProps.endpoint,
				key,
				isEqual: reqProps.isEqual,
			};

			return bindActionCreators(getActionCreators<TValue, TStore>(props.store, creatorProps), dispatch);
		},
		[dispatch],
	);

	return {
		fetch: () => factory.loadMoreItems(count),
		shouldFetch: store.items.length < store.pagination.total,
	};
}
